import React, { useState, useEffect } from 'react';
import InfoTooltip from '../InfoTooltip';
import { rowHelp, fieldHelp } from './constraintHelp';
import SetupRulesSection from './SetupRulesSection';
import { validateTable } from './setupRuleHelpers';

const ConstraintsPanel = ({ constraints, workCenterName, onClose, onApply, disabled }) => {
  const [localConstraints, setLocalConstraints] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newConstraintName, setNewConstraintName] = useState('');
  const [newConstraintPenalty, setNewConstraintPenalty] = useState(1.0);
  const [newConstraintEffort, setNewConstraintEffort] = useState(0.0);
  const [newConstraintOperation, setNewConstraintOperation] = useState('!=');
  const [editingValues, setEditingValues] = useState(null); // Track which constraint's values are being edited
  const [valueFormData, setValueFormData] = useState({ name: '', penalty: '' }); // Form for adding/editing values
  const [transitionFormData, setTransitionFormData] = useState({ from: '', to: '', penalty: '', effort: '' }); // Unified form for adding transitions (penalty + effort)
  const [validColumns, setValidColumns] = useState([]);
  const [loadingColumns, setLoadingColumns] = useState(true);
  // Setup-rule problems, found when Apply Changes is pressed and cleared the
  // moment a rule is touched. Empty while editing — the table is NOT checked
  // as it is typed.
  const [setupRuleErrors, setSetupRuleErrors] = useState([]);

  // Initialize local state from props
  useEffect(() => {
    if (constraints) {
      // Deep copy to avoid mutating props
      console.log('🔍 ConstraintsPanel received constraints:', constraints);
      console.log('🔍 Keys in constraints:', Object.keys(constraints));
      setLocalConstraints(JSON.parse(JSON.stringify(constraints)));
    }
  }, [constraints]);

  // Fetch valid columns on mount
  useEffect(() => {
    const fetchValidColumns = async () => {
      try {
        const apiBaseUrl = process.env.REACT_APP_API_URL || '';
        const response = await fetch(`${apiBaseUrl}/api/config/valid-columns`);
        const result = await response.json();
        
        if (result.success) {
          setValidColumns(result.columns);
          console.log('✅ Valid columns loaded:', result.columns);
        }
      } catch (error) {
        console.error('Error fetching valid columns:', error);
      } finally {
        setLoadingColumns(false);
      }
    };
    
    fetchValidColumns();
  }, []);

  if (!localConstraints) {
    return null;
  }

  const constraintsList = localConstraints.constraints_list || [];

  const handleToggleConstraint = (constraintName) => {
    const updatedList = constraintsList.includes(constraintName)
      ? constraintsList.filter(c => c !== constraintName)
      : [...constraintsList, constraintName];
    
    setLocalConstraints({
      ...localConstraints,
      constraints_list: updatedList
    });
  };

  const handlePenaltyChange = (constraintName, newPenalty) => {
    setLocalConstraints({
      ...localConstraints,
      [constraintName]: {
        ...localConstraints[constraintName],
        penalty: parseFloat(newPenalty) || 0
      }
    });
  };

  const handleEffortChange = (constraintName, newEffort) => {
    const constraint = localConstraints[constraintName];
    const currentEffort = constraint.effort;
    const parsedEffort = parseFloat(newEffort) || 0;

    // If effort is already a dict with transitions, update only the default
    if (currentEffort && typeof currentEffort === 'object' && currentEffort.transitions) {
      setLocalConstraints({
        ...localConstraints,
        [constraintName]: {
          ...constraint,
          effort: {
            ...currentEffort,
            default: parsedEffort
          }
        }
      });
    } else {
      setLocalConstraints({
        ...localConstraints,
        [constraintName]: {
          ...constraint,
          effort: parsedEffort
        }
      });
    }
  };

  const handleOperationChange = (constraintName, newOperation) => {
    setLocalConstraints({
      ...localConstraints,
      [constraintName]: {
        ...localConstraints[constraintName],
        operation: newOperation
      }
    });
  };

  const handleLatenessPenaltyChange = (constraintName, newValue) => {
    setLocalConstraints({
      ...localConstraints,
      [constraintName]: {
        ...localConstraints[constraintName],
        lateness_penalty: parseFloat(newValue) || 0
      }
    });
  };

  // Due-date urgency: extra cost per shift a job waits, per day it is due earlier
  // than the latest-due job. Per work center, because it competes with THIS work
  // center's changeover efforts.
  const handleUrgencyWeightChange = (constraintName, newValue) => {
    setLocalConstraints({
      ...localConstraints,
      [constraintName]: {
        ...localConstraints[constraintName],
        urgency_weight: parseFloat(newValue) || 0
      }
    });
  };

  const handleReset = () => {
    if (constraints) {
      setLocalConstraints(JSON.parse(JSON.stringify(constraints)));
    }
  };

  const handleApply = () => {
    // The setup-time rules are checked HERE and nowhere else. An unfinished
    // rule stops the save and is named; a warning does not stop anything.
    const problems = validateTable(localConstraints.setup_time_rules || [], {
      knownFields: validColumns,
      hasMsaBlock: !!localConstraints.msa_setup_time,
    }).filter(x => x.level === 'error');

    if (problems.length) {
      setSetupRuleErrors(problems);
      return;
    }

    setSetupRuleErrors([]);
    onApply(localConstraints);
    onClose();
  };

  const handleAddConstraint = () => {
    if (!newConstraintName.trim()) {
      alert('Please enter a constraint name');
      return;
    }
    
    const constraintKey = newConstraintName.trim().toLowerCase().replace(/\s+/g, '_');
    
    if (localConstraints[constraintKey]) {
      alert('This constraint already exists');
      return;
    }
    
    // Validate that the column exists in job data
    if (validColumns.length > 0 && !validColumns.includes(constraintKey)) {
      const shouldContinue = window.confirm(
        `Warning: "${constraintKey}" is not a recognized job column.\n\n` +
        `Valid columns are: ${validColumns.join(', ')}\n\n` +
        `This constraint may not work correctly during optimization.\n\n` +
        `Do you want to add it anyway?`
      );
      
      if (!shouldContinue) {
        return;
      }
    }
    
    // Add new constraint to the config
    setLocalConstraints({
      ...localConstraints,
      [constraintKey]: {
        penalty: parseFloat(newConstraintPenalty),
        effort: parseFloat(newConstraintEffort) || 0,
        operation: newConstraintOperation,
        values: null
      },
      constraints_list: [...(localConstraints.constraints_list || []), constraintKey]
    });
    
    // Reset form
    setNewConstraintName('');
    setNewConstraintPenalty(1.0);
    setNewConstraintEffort(0.0);
    setNewConstraintOperation('!=');
    setShowAddForm(false);
  };

  const handleAddValue = (constraintName) => {
    if (!valueFormData.name.trim() || !valueFormData.penalty) {
      alert('Please enter both name and penalty');
      return;
    }

    const constraint = localConstraints[constraintName];
    const currentValues = constraint.values || {};
    
    setLocalConstraints({
      ...localConstraints,
      [constraintName]: {
        ...constraint,
        values: {
          ...currentValues,
          [valueFormData.name.trim()]: parseFloat(valueFormData.penalty)
        }
      }
    });

    setValueFormData({ name: '', penalty: '' });
  };

  const handleDeleteValue = (constraintName, valueName) => {
    const constraint = localConstraints[constraintName];
    const currentValues = { ...(constraint.values || {}) };
    delete currentValues[valueName];
    
    setLocalConstraints({
      ...localConstraints,
      [constraintName]: {
        ...constraint,
        values: Object.keys(currentValues).length > 0 ? currentValues : null
      }
    });
  };

  const handleAddTransition = (constraintName) => {
    if (!transitionFormData.from.trim() || !transitionFormData.to.trim()) {
      alert('Please enter from and to values');
      return;
    }
    if (!transitionFormData.penalty && !transitionFormData.effort) {
      alert('Please enter at least a penalty or effort value');
      return;
    }

    const constraint = localConstraints[constraintName];
    const transitionKey = `${transitionFormData.from.trim()}->${transitionFormData.to.trim()}`;

    // Build updated values with penalty transition
    let updatedValues = { ...(constraint.values || {}) };
    if (transitionFormData.penalty) {
      updatedValues.transitions = {
        ...(updatedValues.transitions || {}),
        [transitionKey]: parseFloat(transitionFormData.penalty)
      };
    }

    // Build updated effort with effort transition
    let updatedEffort = constraint.effort;
    if (transitionFormData.effort) {
      const currentEffort = constraint.effort;
      if (currentEffort && typeof currentEffort === 'object') {
        updatedEffort = {
          ...currentEffort,
          default: currentEffort.default || 0,
          transitions: {
            ...(currentEffort.transitions || {}),
            [transitionKey]: parseFloat(transitionFormData.effort)
          }
        };
      } else {
        updatedEffort = {
          default: currentEffort || 0,
          transitions: {
            [transitionKey]: parseFloat(transitionFormData.effort)
          }
        };
      }
    }

    setLocalConstraints({
      ...localConstraints,
      [constraintName]: {
        ...constraint,
        values: updatedValues,
        effort: updatedEffort
      }
    });

    setTransitionFormData({ from: '', to: '', penalty: '', effort: '' });
  };

  const handleDeleteTransition = (constraintName, transitionKey) => {
    const constraint = localConstraints[constraintName];

    // Remove from values.transitions (penalty)
    const currentValues = { ...(constraint.values || {}) };
    const penaltyTransitions = { ...(currentValues.transitions || {}) };
    delete penaltyTransitions[transitionKey];
    if (Object.keys(penaltyTransitions).length === 0) {
      delete currentValues.transitions;
    } else {
      currentValues.transitions = penaltyTransitions;
    }

    // Remove from effort.transitions
    let updatedEffort = constraint.effort;
    if (updatedEffort && typeof updatedEffort === 'object' && updatedEffort.transitions) {
      const effortTrans = { ...(updatedEffort.transitions || {}) };
      delete effortTrans[transitionKey];
      if (Object.keys(effortTrans).length === 0) {
        updatedEffort = updatedEffort.default || 0;
      } else {
        updatedEffort = { ...updatedEffort, transitions: effortTrans };
      }
    }

    setLocalConstraints({
      ...localConstraints,
      [constraintName]: {
        ...constraint,
        values: Object.keys(currentValues).length > 0 ? currentValues : null,
        effort: updatedEffort
      }
    });
  };

  // Handlers for unified separation rules
  const handleAddSeparationRule = () => {
    const rules = localConstraints.separation || [];
    const newRule = {
      name: `rule_${rules.length + 1}`,
      enabled: true,
      units: 'hours',
      differ_field: 'color',
      match_fields: {},
      gap_value: 3,
      operation: '>'
    };
    setLocalConstraints(prev => ({
      ...prev,
      separation: [...rules, newRule]
    }));
  };

  const handleDeleteSeparationRule = (index) => {
    const rules = [...(localConstraints.separation || [])];
    rules.splice(index, 1);
    setLocalConstraints(prev => ({ ...prev, separation: rules }));
  };

  const handleSeparationChange = (index, field, value) => {
    const rules = [...(localConstraints.separation || [])];
    rules[index] = { ...rules[index], [field]: value };
    setLocalConstraints(prev => ({ ...prev, separation: rules }));
  };

  const handleSeparationMatchFieldChange = (index, field, value) => {
    const rules = [...(localConstraints.separation || [])];
    const matchFields = { ...rules[index].match_fields };
    if (value === '' || value === null || value === undefined) {
      delete matchFields[field];
    } else {
      // Handle boolean values
      if (value === 'true') {
        matchFields[field] = true;
      } else if (value === 'false') {
        matchFields[field] = false;
      } else if (value === 'null') {
        matchFields[field] = null;
      } else {
        matchFields[field] = value;
      }
    }
    rules[index] = { ...rules[index], match_fields: matchFields };
    setLocalConstraints(prev => ({ ...prev, separation: rules }));
  };

  const handleSeparationRemoveMatchField = (index, field) => {
    const rules = [...(localConstraints.separation || [])];
    const matchFields = { ...rules[index].match_fields };
    delete matchFields[field];
    rules[index] = { ...rules[index], match_fields: matchFields };
    setLocalConstraints(prev => ({ ...prev, separation: rules }));
  };

  // Separate special constraint sections from regular constraints
  // `setup_time_rules` is an ARRAY and typeof [] === 'object', so without naming
  // it here it would be rendered as if it were a transition constraint. Same
  // reason `separation` is excluded: both have their own section below.
  const allConstraintNames = Object.keys(localConstraints).filter(
    key => key !== 'constraints_list' && 
           key !== 'separation' &&
           key !== 'setup_time_rules' &&
           typeof localConstraints[key] === 'object' && 
           !/^separation\d+$/.test(key)
  );

  const getPenaltyColor = (penalty) => {
    if (penalty >= 2.0) return '#e74c3c'; // Red - high penalty
    if (penalty >= 1.0) return '#f39c12'; // Orange - medium penalty
    if (penalty >= 0.5) return '#f1c40f'; // Yellow - low-medium penalty
    return '#27ae60'; // Green - low penalty
  };

  const getPenaltyLabel = (penalty) => {
    if (penalty >= 2.0) return 'High';
    if (penalty >= 1.0) return 'Medium';
    if (penalty >= 0.5) return 'Low-Med';
    return 'Low';
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
        width: '95%',
        maxWidth: '1400px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        resize: 'both',
        minWidth: '800px',
        minHeight: '400px'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '2px solid #ecf0f1',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#667eea',
          color: 'white'
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>
              Constraint Configuration
            </h2>
            <p style={{ margin: '4px 0 0 0', fontSize: '12px', opacity: 0.9 }}>
              Work Center {workCenterName}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '0',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '20px 24px'
        }}>
          {/* Add Constraint Button */}
          <div style={{ marginBottom: '16px' }}>
            {!showAddForm ? (
              <button
                onClick={() => setShowAddForm(true)}
                disabled={disabled}
                style={{
                  padding: '8px 16px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'white',
                  backgroundColor: '#667eea',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => !disabled && (e.target.style.backgroundColor = '#5568d3')}
                onMouseLeave={(e) => !disabled && (e.target.style.backgroundColor = '#667eea')}
              >
                + Add New Constraint
              </button>
            ) : (
              <div style={{
                padding: '12px',
                border: '2px solid #667eea',
                borderRadius: '6px',
                backgroundColor: 'rgba(102, 126, 234, 0.05)',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flexWrap: 'wrap'
              }}>
                <input
                  type="text"
                  placeholder="Constraint name (e.g., process_width)"
                  value={newConstraintName}
                  onChange={(e) => setNewConstraintName(e.target.value)}
                  style={{
                    padding: '6px 10px',
                    fontSize: '11px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    minWidth: '200px',
                    flex: '1'
                  }}
                />
                <input
                  type="number"
                  min="0"
                  max="3"
                  step="0.1"
                  value={newConstraintPenalty}
                  onChange={(e) => setNewConstraintPenalty(e.target.value)}
                  style={{
                    width: '70px',
                    padding: '6px 10px',
                    fontSize: '11px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                  title="Penalty (0-3) - affects time"
                />
                <input
                  type="number"
                  min="0"
                  max="10"
                  step="0.5"
                  value={newConstraintEffort}
                  onChange={(e) => setNewConstraintEffort(e.target.value)}
                  style={{
                    width: '70px',
                    padding: '6px 10px',
                    fontSize: '11px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                  title="Effort (0-10) - affects cost only"
                />
                <select
                  value={newConstraintOperation}
                  onChange={(e) => setNewConstraintOperation(e.target.value)}
                  style={{
                    padding: '6px 10px',
                    fontSize: '11px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                >
                  <option value="=">Equals (=)</option>
                  <option value="!=">Not Equals (≠)</option>
                  <option value="<">Less Than (&lt;)</option>
                  <option value=">">Greater Than (&gt;)</option>
                  <option value="<=">Less or Equal (≤)</option>
                  <option value=">=">Greater or Equal (≥)</option>
                </select>
                <button
                  onClick={handleAddConstraint}
                  style={{
                    padding: '6px 12px',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: 'white',
                    backgroundColor: '#27ae60',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Add
                </button>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setNewConstraintName('');
                    setNewConstraintPenalty(1.0);
                    setNewConstraintEffort(0.0);
                    setNewConstraintOperation('!=');
                  }}
                  style={{
                    padding: '6px 12px',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#7f8c8d',
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {allConstraintNames.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#95a5a6' }}>
              No constraints configured for this work center
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {allConstraintNames.map(constraintName => {
                const constraint = localConstraints[constraintName];
                const isEnabled = constraintsList.includes(constraintName);
                const penalty = constraint.penalty || 0;
                const operation = constraint.operation || '!=';
                const values = constraint.values || {};
                const transitions = values?.transitions || {};
                const hasTransitions = Object.keys(transitions).length > 0;
                const effortObj = constraint.effort;
                const effortDefault = (effortObj && typeof effortObj === 'object') ? (effortObj.default || 0) : (effortObj || 0);
                const effortTransitions = (effortObj && typeof effortObj === 'object') ? (effortObj.transitions || {}) : {};
                const hasEffortTransitions = Object.keys(effortTransitions).length > 0;
                const isEditingThisConstraint = editingValues === constraintName;
                const isValidColumn = validColumns.length === 0 || validColumns.includes(constraintName);
                
                return (
                  <div key={constraintName}>
                    <div
                      style={{
                        padding: '12px 16px',
                        border: `2px solid ${isEnabled ? '#667eea' : '#ecf0f1'}`,
                        borderRadius: '6px',
                        backgroundColor: isEnabled ? 'rgba(102, 126, 234, 0.03)' : '#fafafa',
                        opacity: isEnabled ? 1 : 0.6,
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        flexWrap: 'wrap'
                      }}
                    >
                      {/* Checkbox */}
                      <input
                        type="checkbox"
                        checked={isEnabled}
                        onChange={() => handleToggleConstraint(constraintName)}
                        disabled={disabled}
                        style={{
                          width: '18px',
                          height: '18px',
                          cursor: disabled ? 'not-allowed' : 'pointer',
                          flexShrink: 0
                        }}
                      />
                      
                      {/* Constraint Name */}
                      <span style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: isEnabled ? '#2c3e50' : '#7f8c8d',
                        textTransform: 'capitalize',
                        minWidth: '110px',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        {constraintName.replace(/_/g, ' ')}
                        <InfoTooltip content={rowHelp(constraintName)} label={`About the ${constraintName.replace(/_/g, ' ')} constraint`} />
                        {!isValidColumn && (
                          <span
                            style={{
                              fontSize: '10px',
                              padding: '2px 6px',
                              borderRadius: '3px',
                              backgroundColor: '#e74c3c',
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                            title={`Warning: "${constraintName}" is not a recognized job column`}
                          >
                            ⚠️
                          </span>
                        )}
                      </span>
                      
                      {/* Penalty Label Badge */}
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        color: 'white',
                        backgroundColor: getPenaltyColor(penalty),
                        flexShrink: 0
                      }}>
                        {getPenaltyLabel(penalty)}
                      </span>

                      {/* Penalty Input */}
                      {isEnabled && (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                            <label style={{
                              fontSize: '10px',
                              fontWeight: '600',
                              color: '#667eea',
                              whiteSpace: 'nowrap'
                            }}>
                              Penalty:
                            </label>
                            <InfoTooltip content={fieldHelp('penalty', { constraintName, penalty, effort: effortDefault, operation, latenessPenalty: constraint.lateness_penalty || 0 })} label="About Penalty" />
                            <input
                              type="number"
                              min="0"
                              max="3"
                              step="0.1"
                              value={penalty}
                              onChange={(e) => handlePenaltyChange(constraintName, e.target.value)}
                              disabled={disabled}
                              style={{
                                width: '55px',
                                padding: '3px 6px',
                                fontSize: '11px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                textAlign: 'center',
                                cursor: disabled ? 'not-allowed' : 'pointer'
                              }}
                            />
                          </div>

                          {/* Effort Input */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                            <label style={{
                              fontSize: '10px',
                              fontWeight: '600',
                              color: '#e67e22',
                              whiteSpace: 'nowrap'
                            }}>
                              Effort:
                            </label>
                            <InfoTooltip content={fieldHelp('effort', { constraintName, penalty, effort: effortDefault, operation, latenessPenalty: constraint.lateness_penalty || 0 })} label="About Effort" />
                            <input
                              type="number"
                              min="0"
                              max="1000"
                              step="0.5"
                              value={effortDefault}
                              onChange={(e) => handleEffortChange(constraintName, e.target.value)}
                              disabled={disabled}
                              style={{
                                width: '55px',
                                padding: '3px 6px',
                                fontSize: '11px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                textAlign: 'center',
                                cursor: disabled ? 'not-allowed' : 'pointer'
                              }}
                            />
                          </div>

                          {/* Lateness Penalty (only for enddate constraint) */}
                          {constraintName === 'enddate' && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                              <label style={{
                                fontSize: '10px',
                                fontWeight: '600',
                                color: '#e74c3c',
                                whiteSpace: 'nowrap'
                              }}>
                                Late/day:
                              </label>
                              <InfoTooltip content={fieldHelp('lateness', { constraintName, penalty, effort: effortDefault, operation, latenessPenalty: constraint.lateness_penalty || 0 })} label="About Late/day" />
                              <input
                                type="number"
                                min="0"
                                max="20"
                                step="0.5"
                                value={constraint.lateness_penalty || 0}
                                onChange={(e) => handleLatenessPenaltyChange(constraintName, e.target.value)}
                                disabled={disabled}
                                style={{
                                  width: '55px',
                                  padding: '3px 6px',
                                  fontSize: '11px',
                                  border: '1px solid #e74c3c',
                                  borderRadius: '4px',
                                  textAlign: 'center',
                                  cursor: disabled ? 'not-allowed' : 'pointer'
                                }}
                              />
                              <label
                                title="Extra cost per shift a job waits, for each day it is due earlier than the latest-due job here. Pulls urgent work forward. 0 = all jobs weighted equally. Unlike Effort, which only penalises two neighbours running out of order, this grows the longer a job waits."
                                style={{
                                  fontSize: '10px',
                                  fontWeight: '600',
                                  color: '#2980b9',
                                  whiteSpace: 'nowrap',
                                  marginLeft: '6px'
                                }}
                              >
                                Urgency:
                              </label>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.5"
                                value={constraint.urgency_weight || 0}
                                onChange={(e) => handleUrgencyWeightChange(constraintName, e.target.value)}
                                disabled={disabled}
                                style={{
                                  width: '55px',
                                  padding: '3px 6px',
                                  fontSize: '11px',
                                  border: '1px solid #2980b9',
                                  borderRadius: '4px',
                                  textAlign: 'center',
                                  cursor: disabled ? 'not-allowed' : 'pointer'
                                }}
                              />
                            </div>
                          )}

                          {/* Operation Dropdown */}
                          {constraint.operation && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                              <label style={{
                                fontSize: '10px',
                                fontWeight: '600',
                                color: '#667eea',
                                whiteSpace: 'nowrap'
                              }}>
                                Op:
                              </label>
                              <InfoTooltip content={fieldHelp('operation', { constraintName, penalty, effort: effortDefault, operation, latenessPenalty: constraint.lateness_penalty || 0 })} label="About Op" />
                              <select
                                value={operation}
                                onChange={(e) => handleOperationChange(constraintName, e.target.value)}
                                disabled={disabled}
                                style={{
                                  padding: '3px 6px',
                                  fontSize: '11px',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px',
                                  cursor: disabled ? 'not-allowed' : 'pointer'
                                }}
                              >
                                <option value="=">Equals (=)</option>
                                <option value="!=">Not Equals (≠)</option>
                                <option value="<">Less Than (&lt;)</option>
                                <option value=">">Greater Than (&gt;)</option>
                                <option value="<=">Less or Equal (≤)</option>
                                <option value=">=">Greater or Equal (≥)</option>
                              </select>
                            </div>
                          )}

                          {/* Values Display/Edit */}
                          <div style={{ 
                            flex: 1, 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            fontSize: '10px',
                            color: '#7f8c8d'
                          }}>
                            <button
                              onClick={() => setEditingValues(isEditingThisConstraint ? null : constraintName)}
                              disabled={disabled}
                              style={{
                                padding: '4px 10px',
                                fontSize: '10px',
                                fontWeight: '600',
                                color: isEditingThisConstraint ? 'white' : '#667eea',
                                backgroundColor: isEditingThisConstraint ? '#667eea' : 'white',
                                border: '1px solid #667eea',
                                borderRadius: '4px',
                                cursor: disabled ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {isEditingThisConstraint ? '▲ Close' : '▼ Transitions'}
                              {!isEditingThisConstraint && (hasTransitions || hasEffortTransitions) && (() => {
                                const allKeys = new Set([...Object.keys(transitions), ...Object.keys(effortTransitions)]);
                                return (
                                  <span style={{
                                    padding: '1px 5px',
                                    borderRadius: '8px',
                                    backgroundColor: '#667eea',
                                    color: 'white',
                                    fontSize: '9px',
                                    fontWeight: 'bold'
                                  }}>
                                    {allKeys.size}
                                  </span>
                                );
                              })()}
                            </button>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Unified Transitions Editor (appears below constraint when editing) */}
                    {isEditingThisConstraint && isEnabled && (() => {
                      // Merge all transition keys from penalty and effort
                      const allTransitionKeys = [...new Set([...Object.keys(transitions), ...Object.keys(effortTransitions)])];
                      return (
                        <div style={{
                          marginTop: '8px',
                          marginLeft: '30px',
                          padding: '12px',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          backgroundColor: '#f8f9fa'
                        }}>
                          <div style={{ fontSize: '11px', fontWeight: '600', color: '#667eea', marginBottom: '8px' }}>
                            Transition Rules <span style={{ fontWeight: '400', fontSize: '10px', color: '#95a5a6' }}>(use 'any' as wildcard)</span>
                          </div>

                          {/* Existing transitions as unified rows */}
                          {allTransitionKeys.length > 0 && (
                            <div style={{ marginBottom: '10px' }}>
                              {/* Header row */}
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '4px 6px',
                                fontSize: '9px',
                                fontWeight: '700',
                                color: '#95a5a6',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}>
                                <span style={{ minWidth: '140px' }}>Transition</span>
                                <span style={{ width: '65px', textAlign: 'center' }}>Penalty</span>
                                <span style={{ width: '65px', textAlign: 'center' }}>Effort</span>
                                <span style={{ width: '50px' }}></span>
                              </div>
                              {allTransitionKeys.map(transitionKey => {
                                const penaltyVal = transitions[transitionKey];
                                const effortVal = effortTransitions[transitionKey];
                                return (
                                  <div key={transitionKey} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '5px 6px',
                                    marginBottom: '3px',
                                    backgroundColor: '#eef0fb',
                                    borderRadius: '4px',
                                    fontSize: '11px'
                                  }}>
                                    <span style={{ fontWeight: '600', minWidth: '140px', fontFamily: 'monospace', fontSize: '10px' }}>
                                      {transitionKey}
                                    </span>
                                    <span style={{
                                      width: '65px',
                                      textAlign: 'center',
                                      fontWeight: 'bold',
                                      color: penaltyVal != null ? getPenaltyColor(penaltyVal) : '#bdc3c7',
                                      fontSize: '10px'
                                    }}>
                                      {penaltyVal != null ? `${penaltyVal}h` : '—'}
                                    </span>
                                    <span style={{
                                      width: '65px',
                                      textAlign: 'center',
                                      fontWeight: 'bold',
                                      color: effortVal != null ? '#e67e22' : '#bdc3c7',
                                      fontSize: '10px'
                                    }}>
                                      {effortVal != null ? effortVal : '—'}
                                    </span>
                                    <button
                                      onClick={() => handleDeleteTransition(constraintName, transitionKey)}
                                      disabled={disabled}
                                      style={{
                                        padding: '2px 8px',
                                        fontSize: '10px',
                                        color: '#e74c3c',
                                        backgroundColor: 'white',
                                        border: '1px solid #e74c3c',
                                        borderRadius: '3px',
                                        cursor: disabled ? 'not-allowed' : 'pointer'
                                      }}
                                    >
                                      ✕
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Add New Transition Form */}
                          <div style={{
                            padding: '8px',
                            backgroundColor: 'rgba(102, 126, 234, 0.04)',
                            borderRadius: '4px',
                            border: '1px dashed #667eea'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                              <input
                                type="text"
                                placeholder="From"
                                value={transitionFormData.from}
                                onChange={(e) => setTransitionFormData({ ...transitionFormData, from: e.target.value })}
                                disabled={disabled}
                                style={{
                                  width: '70px',
                                  padding: '5px 6px',
                                  fontSize: '11px',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px'
                                }}
                              />
                              <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#667eea' }}>→</span>
                              <input
                                type="text"
                                placeholder="To"
                                value={transitionFormData.to}
                                onChange={(e) => setTransitionFormData({ ...transitionFormData, to: e.target.value })}
                                disabled={disabled}
                                style={{
                                  width: '70px',
                                  padding: '5px 6px',
                                  fontSize: '11px',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px'
                                }}
                              />
                              <input
                                type="number"
                                placeholder="Penalty"
                                min="0"
                                max="3"
                                step="0.1"
                                value={transitionFormData.penalty}
                                onChange={(e) => setTransitionFormData({ ...transitionFormData, penalty: e.target.value })}
                                disabled={disabled}
                                style={{
                                  width: '60px',
                                  padding: '5px 6px',
                                  fontSize: '11px',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px'
                                }}
                              />
                              <input
                                type="number"
                                placeholder="Effort"
                                min="0"
                                max="1000"
                                step="1"
                                value={transitionFormData.effort}
                                onChange={(e) => setTransitionFormData({ ...transitionFormData, effort: e.target.value })}
                                disabled={disabled}
                                style={{
                                  width: '60px',
                                  padding: '5px 6px',
                                  fontSize: '11px',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px'
                                }}
                              />
                              <button
                                onClick={() => handleAddTransition(constraintName)}
                                disabled={disabled}
                                style={{
                                  padding: '5px 10px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  color: 'white',
                                  backgroundColor: '#667eea',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: disabled ? 'not-allowed' : 'pointer'
                                }}
                              >
                                Add
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          )}

          {/* ─── Setup Time Rules Section ─── */}
          <SetupRulesSection
            wcId={workCenterName}
            rules={localConstraints.setup_time_rules || []}
            onChange={(rules) => {
              setSetupRuleErrors([]);   // editing answers the complaint
              setLocalConstraints(prev => ({ ...prev, setup_time_rules: rules }));
            }}
            disabled={disabled}
            knownFields={validColumns}
            hasMsaBlock={!!localConstraints.msa_setup_time}
            msaSettings={localConstraints.msa_setup_time || null}
            issues={setupRuleErrors}
          />

          {/* ─── Separation Rules Section (Unified) ─── */}
          <div style={{ marginTop: '24px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '12px'
            }}>
              <h3 style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#1abc9c' }}>
                Separation Rules
              </h3>
              <button
                onClick={handleAddSeparationRule}
                disabled={disabled}
                style={{
                  padding: '5px 12px',
                  fontSize: '11px',
                  fontWeight: '600',
                  color: 'white',
                  backgroundColor: '#1abc9c',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: disabled ? 'not-allowed' : 'pointer'
                }}
              >
                + Add Rule
              </button>
            </div>

            {(!localConstraints.separation || localConstraints.separation.length === 0) ? (
              <div style={{ fontSize: '11px', color: '#95a5a6', fontStyle: 'italic', padding: '8px 0' }}>
                No separation rules configured
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {localConstraints.separation.map((rule, idx) => {
                  const units = rule.units || 'hours';
                  const isHours = units === 'hours';
                  const themeColor = isHours ? '#1abc9c' : '#8e44ad';
                  
                  return (
                    <div key={idx} style={{
                      padding: '12px 16px',
                      border: `2px solid ${rule.enabled ? themeColor : '#dfe6e9'}`,
                      borderRadius: '6px',
                      backgroundColor: rule.enabled ? (isHours ? 'rgba(26,188,156,0.03)' : 'rgba(142,68,173,0.03)') : '#fafafa',
                      opacity: rule.enabled ? 1 : 0.65
                    }}>
                      {/* Header */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                        <input
                          type="checkbox"
                          checked={rule.enabled || false}
                          onChange={(e) => handleSeparationChange(idx, 'enabled', e.target.checked)}
                          disabled={disabled}
                          style={{ width: '16px', height: '16px', cursor: disabled ? 'not-allowed' : 'pointer' }}
                        />
                        <input
                          type="text"
                          value={rule.name || ''}
                          onChange={(e) => handleSeparationChange(idx, 'name', e.target.value)}
                          placeholder="Rule name"
                          disabled={disabled}
                          style={{ flex: 1, fontSize: '12px', fontWeight: '700', padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                        <select
                          value={units}
                          onChange={(e) => handleSeparationChange(idx, 'units', e.target.value)}
                          disabled={disabled}
                          style={{ padding: '4px 8px', fontSize: '11px', fontWeight: '600', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: 'white' }}
                        >
                          <option value="hours">Hours</option>
                          <option value="shifts">Shifts</option>
                        </select>
                        <button
                          onClick={() => handleDeleteSeparationRule(idx)}
                          disabled={disabled}
                          style={{
                            padding: '2px 8px',
                            fontSize: '10px',
                            color: '#e74c3c',
                            backgroundColor: 'white',
                            border: '1px solid #e74c3c',
                            borderRadius: '3px',
                            cursor: disabled ? 'not-allowed' : 'pointer'
                          }}
                        >
                          Delete
                        </button>
                      </div>

                      {/* Fields grid */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', marginBottom: '10px' }}>
                        {/* differ_field - only for hours */}
                        {isHours && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <label style={{ fontSize: '10px', fontWeight: '600', color: themeColor, minWidth: '90px' }}>
                              Differ Field:
                            </label>
                            <input
                              type="text"
                              placeholder="e.g. color"
                              value={rule.differ_field || ''}
                              onChange={(e) => handleSeparationChange(idx, 'differ_field', e.target.value)}
                              disabled={disabled}
                              style={{ flex: 1, padding: '4px 7px', fontSize: '11px', border: '1px solid #ddd', borderRadius: '4px' }}
                            />
                          </div>
                        )}

                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <label style={{ fontSize: '10px', fontWeight: '600', color: themeColor, minWidth: '90px' }}>
                            Gap Value:
                          </label>
                          <input
                            type="number"
                            min={isHours ? "0.5" : "1"}
                            step={isHours ? "0.5" : "1"}
                            value={rule.gap_value || (isHours ? 3 : 2)}
                            onChange={(e) => handleSeparationChange(idx, 'gap_value', isHours ? parseFloat(e.target.value) : parseInt(e.target.value))}
                            disabled={disabled}
                            style={{ width: '65px', padding: '4px 7px', fontSize: '11px', border: '1px solid #ddd', borderRadius: '4px', textAlign: 'center' }}
                          />
                          <span style={{ fontSize: '10px', color: '#7f8c8d' }}>{isHours ? 'h' : 'shifts'}</span>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <label style={{ fontSize: '10px', fontWeight: '600', color: themeColor, minWidth: '90px' }}>
                            Operation:
                          </label>
                          <select
                            value={rule.operation || '>'}
                            onChange={(e) => handleSeparationChange(idx, 'operation', e.target.value)}
                            disabled={disabled}
                            style={{ padding: '4px 7px', fontSize: '11px', border: '1px solid #ddd', borderRadius: '4px' }}
                          >
                            <option value="<">{'<'} (less than)</option>
                            <option value=">">{'>'} (greater than)</option>
                            <option value="=">= (equal to)</option>
                          </select>
                        </div>
                      </div>

                      {/* Match Fields */}
                      <div style={{ marginTop: '10px' }}>
                        <div style={{ fontSize: '10px', fontWeight: '600', color: themeColor, marginBottom: '5px' }}>
                          Match Fields ({isHours ? 'both jobs must match' : 'applies to dependency pairs'}):
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {Object.entries(rule.match_fields || {}).map(([field, value]) => (
                            <div key={field} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <input
                                type="text"
                                value={field}
                                placeholder="Field name"
                                disabled
                                style={{ flex: 1, padding: '4px 7px', fontSize: '11px', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f5f5f5' }}
                              />
                              <span style={{ fontSize: '11px', minWidth: '10px', textAlign: 'center' }}>:</span>
                              <input
                                type="text"
                                value={value === true ? 'true' : value === false ? 'false' : value === null ? 'null' : value}
                                onChange={(e) => handleSeparationMatchFieldChange(idx, field, e.target.value)}
                                placeholder="Value (true/false/null/text)"
                                disabled={disabled}
                                style={{ flex: 1, padding: '4px 7px', fontSize: '11px', border: '1px solid #ddd', borderRadius: '4px' }}
                              />
                              <button
                                onClick={() => handleSeparationRemoveMatchField(idx, field)}
                                disabled={disabled}
                                style={{ padding: '2px 8px', fontSize: '10px', color: '#e74c3c', backgroundColor: 'white', border: '1px solid #e74c3c', borderRadius: '3px', cursor: disabled ? 'not-allowed' : 'pointer' }}
                              >×</button>
                            </div>
                          ))}
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <input
                              type="text"
                              placeholder="Field name"
                              id={`sep_field_${idx}`}
                              disabled={disabled}
                              style={{ flex: 1, padding: '4px 7px', fontSize: '11px', border: '1px solid #ddd', borderRadius: '4px' }}
                            />
                            <input
                              type="text"
                              placeholder="Value (true/false/null/text)"
                              id={`sep_value_${idx}`}
                              disabled={disabled}
                              style={{ flex: 1, padding: '4px 7px', fontSize: '11px', border: '1px solid #ddd', borderRadius: '4px' }}
                            />
                            <button
                              onClick={() => {
                                const field = document.getElementById(`sep_field_${idx}`).value.trim();
                                const value = document.getElementById(`sep_value_${idx}`).value.trim();
                                if (field) {
                                  handleSeparationMatchFieldChange(idx, field, value);
                                  document.getElementById(`sep_field_${idx}`).value = '';
                                  document.getElementById(`sep_value_${idx}`).value = '';
                                }
                              }}
                              disabled={disabled}
                              style={{ padding: '4px 12px', fontSize: '11px', fontWeight: '600', color: 'white', backgroundColor: themeColor, border: 'none', borderRadius: '4px', cursor: disabled ? 'not-allowed' : 'pointer' }}
                            >Add</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Why the save did not go through. Shown ONLY after Apply was
            pressed, and loud when it is — it has to be the thing you see. */}
        {setupRuleErrors.length > 0 && (
          <div style={{
            margin: '0 24px', padding: '14px 16px', lineHeight: 1.5,
            color: 'white', backgroundColor: '#c0392b', borderRadius: '6px',
            boxShadow: '0 2px 10px rgba(192,57,43,0.4)'
          }}>
            <div style={{ fontSize: '15px', fontWeight: 700 }}>
              ✕ NOT SAVED — the setup time rules are not finished
            </div>
            {setupRuleErrors.map((issue, n) => (
              <div key={n} style={{ marginTop: '5px', fontSize: '13px' }}>
                {issue.index === null ? '' : `Line ${issue.index + 1}: `}{issue.message}
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '2px solid #ecf0f1',
          display: 'flex',
          justifyContent: 'space-between',
          backgroundColor: '#f8f9fa'
        }}>
          <button
            onClick={handleReset}
            disabled={disabled}
            style={{
              padding: '10px 20px',
              fontSize: '13px',
              fontWeight: '600',
              color: '#7f8c8d',
              backgroundColor: 'white',
              border: '1px solid #ddd',
              borderRadius: '6px',
              cursor: disabled ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              if (!disabled) {
                e.target.style.borderColor = '#667eea';
                e.target.style.color = '#667eea';
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = '#ddd';
              e.target.style.color = '#7f8c8d';
            }}
          >
            Reset to Defaults
          </button>
          
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={onClose}
              style={{
                padding: '10px 20px',
                fontSize: '13px',
                fontWeight: '600',
                color: '#7f8c8d',
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#95a5a6';
                e.target.style.color = '#2c3e50';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#ddd';
                e.target.style.color = '#7f8c8d';
              }}
            >
              Cancel
            </button>
            
            <button
              onClick={handleApply}
              disabled={disabled}
              style={{
                padding: '10px 20px',
                fontSize: '13px',
                fontWeight: '600',
                color: 'white',
                backgroundColor: disabled ? '#95a5a6' : '#667eea',
                border: 'none',
                borderRadius: '6px',
                cursor: disabled ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                boxShadow: disabled ? 'none' : '0 2px 8px rgba(102, 126, 234, 0.3)'
              }}
              onMouseEnter={(e) => {
                if (!disabled) {
                  e.target.style.backgroundColor = '#5a67d8';
                  e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!disabled) {
                  e.target.style.backgroundColor = '#667eea';
                  e.target.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.3)';
                }
              }}
            >
              Apply Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConstraintsPanel;
